/**
 *
 * The event duck. Default export is the reducer
 *
 * Action Types:
 * - CLEAR_EVENT
 * - EVENT_CREATED
 * - FETCH_FAILED
 * - FETCH_REQUESTED
 * - FETCH_SUCCEEDED
 * - NEW_FAILED
 * - NEW_REQUESTED
 * - NEW_SUCCEEDED
 * - SEND_FAILED
 * - SEND_REQUESTED
 * - SEND_SUCCEEDED
 * - UPDATE_DETAILS_FAILED
 * - UPDATE_DETAILS_REQUESTED
 * - UPDATE_DETAILS_SUCCEEDED
 *
 * Action Creators:
 * - clearEvent
 * - eventCreated
 * - fetchEventFailed
 * - fetchEventRequest
 * - fetchEventSucceeded
 * - newEventFailed
 * - newEventRequest
 * - newEventSucceeded
 * - sendEventFailed
 * - sendEventRequest
 * - sendEventSucceeded
 * - updateDetailsFailed
 * - updateDetailsRequest
 * - updateDetailsSucceeded
 */

import type {
  EventsState,
  EventsAction,
  FetchEventFailed,
  FetchEventRequested,
  FetchEventSucceeded,
  NewEventFailed,
  NewEventRequested,
  NewEventSucceeded,
  EventUpdateDetailsFailed,
  EventUpdateDetailsRequested,
  EventUpdateDetailsSucceeded,
  EventSendFailed,
  EventSendRequested,
  EventSendSucceeded,
  EventApiResponse,
  CheckPrice,
  ClearEvent,
} from '../types/event';

/** @type {ActionType} */
export const CHECK_PRICE = 'PB/event/CHECK_PRICE';

/** @type {ActionType} */
export const CLEAR_EVENT = 'PB/event/CLEAR_EVENT';

/** @type {ActionType} */
export const EVENT_CREATED = 'PB/event/EVENT_CREATED';

/** @type {ActionType} */
export const FETCH_FAILED = 'PB/event/FETCH_FAILED';

/** @type {ActionType} */
export const FETCH_REQUESTED = 'PB/event/FETCH_REQUESTED';

/** @type {ActionType} */
export const FETCH_SUCCEEDED = 'PB/event/FETCH_SUCCEEDED';

/** @type {ActionType} */
export const NEW_FAILED = 'PB/event/NEW_FAILED';

/** @type {ActionType} */
export const NEW_REQUESTED = 'PB/event/NEW_REQUESTED';

/** @type {ActionType} */
export const NEW_SUCCEEDED = 'PB/event/NEW_SUCCEEDED';

/** @type {ActionType} */
export const SEND_FAILED = 'PB/event/SEND_FAILED';

/** @type {ActionType} */
export const SEND_REQUESTED = 'PB/event/SEND_REQUESTED';

/** @type {ActionType} */
export const SEND_SUCCEEDED = 'PB/event/SEND_SUCCEEDED';

/** @type {ActionType} */
export const UPDATE_DETAILS_FAILED = 'PB/event/UPDATE_DETAILS_FAILED';

/** @type {ActionType} */
export const UPDATE_DETAILS_REQUESTED = 'PB/event/UPDATE_DETAILS_REQUESTED';

/** @type {ActionType} */
export const UPDATE_DETAILS_SUCCEEDED = 'PB/event/UPDATE_DETAILS_SUCCEEDED';

export const initialState = {
  event: {},
  accountInfo: {},
  designInfo: {},
  status: {
    loadingEvent: false,
    sendingEvent: false,
    updatingDetails: false,
  },
};

/** @ignore */
export default function event(
  state: EventsState = initialState,
  action: EventsAction,
): EventsState {
  switch (action.type) {
    case NEW_SUCCEEDED:
    case FETCH_SUCCEEDED: {
      const { payload } = action;

      return {
        event: payload.event || {},
        accountInfo: payload.accountInfo || {},
        designInfo: payload.designInfo || {},
        status: {
          ...state.status,
          loadingEvent: false,
        },
      };
    }

    case FETCH_FAILED: {
      return {
        ...state,
        status: {
          ...state.status,
          loadingEvent: false,
        },
      };
    }

    case FETCH_REQUESTED: {
      return {
        ...state,
        status: {
          ...state.status,
          loadingEvent: true,
        },
      };
    }

    case SEND_FAILED:
    case SEND_SUCCEEDED: {
      let { status } = state;
      status = { ...status, sendingEvent: false };
      return { ...state, status };
    }

    case SEND_REQUESTED: {
      let { status } = state;
      status = { ...status, sendingEvent: true };
      return { ...state, status };
    }

    case UPDATE_DETAILS_FAILED:
    case UPDATE_DETAILS_SUCCEEDED: {
      let { status } = state;
      status = { ...status, updatingDetails: false };
      return { ...state, status };
    }

    case UPDATE_DETAILS_REQUESTED: {
      let { status } = state;
      status = { ...status, updatingDetails: true };
      return { ...state, status };
    }

    case CLEAR_EVENT:
      return { ...initialState };

    default:
      return state;
  }
}

/**
 * This action indicates that the event/guests have been loaded and a price calculation
 * can be performed.
 *
 * Depending on the result of the price check, the user may or may not be redirected/
 * a modal may appear.
 */

export function checkPrice(): CheckPrice {
  return {
    type: CHECK_PRICE,
  };
}

/**
 * This action clears the event.
 *
 * @return {Action}
 * @property {string} type CLEAR_EVENT
 */
export function clearEvent(): ClearEvent {
  return {
    type: CLEAR_EVENT,
  };
}

/**
 * This action signifies that were unable to fetch an event.
 * Most likely meaning that the API is down.
 *
 * @return {Action}
 * @property {string} type REQUEST_FAILED
 * @property {Object} payload
 */
export function fetchEventFailed(message: string): FetchEventFailed {
  return {
    type: FETCH_FAILED,
    payload: { message },
  };
}

/**
 * This action signifies that we have requested the event.
 *
 * @return {Action}
 * @property {string} type REQUESTED
 */
export function fetchEventRequest(id: string): FetchEventRequested {
  return {
    type: FETCH_REQUESTED,
    payload: { id },
  };
}

/**
 * This action signifies that we have properly retrieved an event.
 * In the form of an object with a all the data needed to build a viewport instance.
 *
 * @param {Object} event - data needed to build a viewport instance.
 * @return {Action}
 * @property {string} type REQUEST_SUCCEEDED
 * @property {Object} payload
 */
export function fetchEventSucceeded(
  data: EventApiResponse,
): FetchEventSucceeded {
  return {
    type: FETCH_SUCCEEDED,
    payload: data,
  };
}

/**
 * This action signifies that were unable to fetch an event.
 * Most likely meaning that the API is down.
 *
 * @return {Action}
 * @property {string} type REQUEST_FAILED
 * @property {Object} payload
 */
export function newEventFailed(message: string): NewEventFailed {
  return {
    type: NEW_FAILED,
    payload: { message },
  };
}

/**
 * This action signifies that we have requested the event.
 *
 * @return {Action}
 * @property {string} type REQUESTED
 */
export function newEventRequest(packageId: number): NewEventRequested {
  return {
    type: NEW_REQUESTED,
    payload: { packageId },
  };
}

/**
 * This action signifies that we have properly retrieved an event.
 * In the form of an object with a all the data needed to build a viewport instance.
 *
 * @param {Object} event - data needed to build a viewport instance.
 * @return {Action}
 * @property {string} type REQUEST_SUCCEEDED
 * @property {Object} payload
 */
export function newEventSucceeded(data: EventApiResponse): NewEventSucceeded {
  return {
    type: NEW_SUCCEEDED,
    payload: data,
  };
}

/**
 * This action signifies that the detail update has failed.
 *
 * @return {Action}
 * @property {string} type SEND_EVENT_FAILED
 */
export function sendEventFailed(eventId: string): EventSendFailed {
  return {
    type: SEND_FAILED,
    payload: { eventId },
  };
}

/**
 * This action signifies that we have requested the event details be updated.
 *
 * @return {Action}
 * @property {string} type SEND_EVENT_REQUESTED
 */
export function sendEventRequest(eventId: string): EventSendRequested {
  return {
    type: SEND_REQUESTED,
    payload: { eventId },
  };
}

/**
 * This action signifies that the detail update has succeeded.
 *
 * @return {Action}
 * @property {string} type SEND_EVENT_SUCCEEDED
 */
export function sendEventSucceeded(eventId: string): EventSendSucceeded {
  return {
    type: SEND_SUCCEEDED,
    payload: { eventId },
  };
}

/**
 * This action signifies that the detail update has failed.
 *
 * @return {Action}
 * @property {string} type UPDATE_DETAILS_FAILED
 */
export function updateDetailsFailed(eventId: string): EventUpdateDetailsFailed {
  return {
    type: UPDATE_DETAILS_FAILED,
    payload: { eventId },
  };
}

/**
 * This action signifies that we have requested the event details be updated.
 *
 * @return {Action}
 * @property {string} type UPDATE_DETAILS_REQUESTED
 */
export function updateDetailsRequest(
  eventId: string,
  from: string,
  subject: string,
  chainSend: boolean,
): EventUpdateDetailsRequested {
  return {
    type: UPDATE_DETAILS_REQUESTED,
    payload: { chainSend, eventId, from, subject },
  };
}

/**
 * This action signifies that the detail update has succeeded.
 *
 * @return {Action}
 * @property {string} type UPDATE_DETAILS_SUCCEEDED
 */
export function updateDetailsSucceeded(
  eventId: string,
  chainSend: boolean,
): EventUpdateDetailsSucceeded {
  return {
    type: UPDATE_DETAILS_SUCCEEDED,
    payload: { eventId, chainSend },
  };
}

/**
 * This action signifies that an event has been created (persisted) for the first time
 *
 * @return {Action}
 * @property {string} type EVENT_CREATED
 */
export function eventCreated(eventId: string) {
  return {
    type: EVENT_CREATED,
    payload: { eventId },
  };
}
